import { create } from "zustand";
import {
  getCustomers,
  getCustomerMessages,
  toggleAIMode,
  sendManualMessage,
  confirmOrderManual,
  cancelOrderManual,
  deleteConversation as deleteConversationApi,
  type CustomerSummary,
  type CustomerConversation,
  type UnifiedMessage,
  type ConfirmationStatus,
} from "../services/whatsapp/whatsappService";

export type CustomerFilterTab = "all" | ConfirmationStatus;

interface CustomerChatState {
  // State
  customers: CustomerSummary[];
  customersLoading: boolean;
  selectedPhone: string | null;
  customerConversation: CustomerConversation | null;
  conversationLoading: boolean;
  searchQuery: string;
  activeFilter: CustomerFilterTab;

  // Actions
  fetchCustomers: (ownerId: string, search?: string) => Promise<void>;
  selectCustomer: (phone: string, ownerId: string) => Promise<void>;
  setFilter: (filter: CustomerFilterTab) => void;
  setSearchQuery: (query: string) => void;

  // Delegate to existing per-confirmation endpoints
  toggleAI: (confirmationId: number, aiEnabled: boolean) => Promise<void>;
  sendMessage: (confirmationId: number, message: string) => Promise<void>;
  confirmOrder: (confirmationId: number, ownerId: string) => Promise<void>;
  cancelOrder: (confirmationId: number, ownerId: string) => Promise<void>;
  deleteChat: (confirmationId: number) => Promise<void>;

  // Firebase updates
  updateMessagesFromFirebase: (data: { messages: any[]; status: string; ai_enabled: boolean }, confirmationId: number) => void;
  updateCustomerStatusFromFirebase: (phone: string, status: ConfirmationStatus) => void;

  reset: () => void;
}

export const useCustomerChatStore = create<CustomerChatState>((set, get) => ({
  customers: [],
  customersLoading: false,
  selectedPhone: null,
  customerConversation: null,
  conversationLoading: false,
  searchQuery: "",
  activeFilter: "all",

  fetchCustomers: async (ownerId, search) => {
    set({ customersLoading: true });
    try {
      const customers = await getCustomers(ownerId, search);
      set({ customers, customersLoading: false });
    } catch {
      set({ customers: [], customersLoading: false });
    }
  },

  selectCustomer: async (phone, ownerId) => {
    set({ selectedPhone: phone, conversationLoading: true });
    try {
      const conversation = await getCustomerMessages(phone, ownerId);
      set({ customerConversation: conversation, conversationLoading: false });
    } catch {
      set({ customerConversation: null, conversationLoading: false });
    }
  },

  setFilter: (filter) => set({ activeFilter: filter }),
  setSearchQuery: (query) => set({ searchQuery: query }),

  toggleAI: async (confirmationId, aiEnabled) => {
    await toggleAIMode(confirmationId, aiEnabled);
    const conv = get().customerConversation;
    if (conv) {
      const orders = conv.orders.map((o) =>
        o.confirmation_id === confirmationId ? { ...o, ai_enabled: aiEnabled } : o
      );
      set({ customerConversation: { ...conv, orders } });
    }
  },

  sendMessage: async (confirmationId, message) => {
    const conv = get().customerConversation;
    if (!conv) return;

    // Optimistic: add sending message
    const optimistic: UnifiedMessage = {
      role: "assistant",
      content: message,
      timestamp: new Date().toISOString(),
      status: "sending",
      order_id: conv.orders.find((o) => o.confirmation_id === confirmationId)?.order_id || 0,
      confirmation_id: confirmationId,
    };
    set({
      customerConversation: {
        ...conv,
        messages: [...conv.messages, optimistic],
      },
    });

    try {
      const result = await sendManualMessage(confirmationId, message);
      // Replace optimistic with server messages
      const current = get().customerConversation;
      if (current) {
        const withoutOptimistic = current.messages.filter((m) => m !== optimistic);
        const serverMsgs: UnifiedMessage[] = result.messages.map((m: any) => ({
          ...m,
          status: m.status || "sent",
          order_id: optimistic.order_id,
          confirmation_id: confirmationId,
        }));
        set({
          customerConversation: {
            ...current,
            messages: [...withoutOptimistic, ...serverMsgs],
          },
        });
      }
    } catch {
      const current = get().customerConversation;
      if (current) {
        const msgs = current.messages.map((m) =>
          m === optimistic ? { ...m, status: "failed" as const } : m
        );
        set({ customerConversation: { ...current, messages: msgs } });
      }
    }
  },

  confirmOrder: async (confirmationId, ownerId) => {
    await confirmOrderManual(confirmationId);
    const phone = get().selectedPhone;
    if (phone) {
      const conv = await getCustomerMessages(phone, ownerId);
      set({ customerConversation: conv });
    }
  },

  cancelOrder: async (confirmationId, ownerId) => {
    await cancelOrderManual(confirmationId);
    const phone = get().selectedPhone;
    if (phone) {
      const conv = await getCustomerMessages(phone, ownerId);
      set({ customerConversation: conv });
    }
  },

  deleteChat: async (confirmationId) => {
    await deleteConversationApi(confirmationId);
    const { customers } = get();
    // Refresh the customer list
    set({
      customers: customers.map((c) =>
        c.latest_confirmation_id === confirmationId
          ? { ...c, order_count: Math.max(0, c.order_count - 1) }
          : c
      ),
    });
  },

  updateMessagesFromFirebase: (data, confirmationId) => {
    const conv = get().customerConversation;
    if (!conv) return;

    // Find the order this update belongs to
    const order = conv.orders.find((o) => o.confirmation_id === confirmationId);
    if (!order) return;

    // Replace messages for this confirmation with new ones
    const otherMessages = conv.messages.filter((m) => m.confirmation_id !== confirmationId);
    const newMessages: UnifiedMessage[] = (data.messages || []).map((m: any) => ({
      ...m,
      order_id: order.order_id,
      confirmation_id: confirmationId,
    }));

    const allMessages = [...otherMessages, ...newMessages].sort(
      (a, b) => (a.timestamp || "").localeCompare(b.timestamp || "")
    );

    const orders = conv.orders.map((o) =>
      o.confirmation_id === confirmationId
        ? { ...o, status: data.status as ConfirmationStatus, ai_enabled: data.ai_enabled }
        : o
    );

    set({ customerConversation: { ...conv, messages: allMessages, orders } });
  },

  updateCustomerStatusFromFirebase: (phone, status) => {
    const { customers } = get();
    set({
      customers: customers.map((c) =>
        c.customer_phone === phone ? { ...c, latest_status: status } : c
      ),
    });
  },

  reset: () =>
    set({
      customers: [],
      selectedPhone: null,
      customerConversation: null,
      searchQuery: "",
      activeFilter: "all",
    }),
}));
